/**
 * CompanyNotesPanel — lightweight internal notes on a company.
 *
 * Used inside the Pipeline expanded row (next to SavedInvestigationsPanel).
 * Tenant-safe server-side. Notes are for pursuit context, not collaboration.
 *
 * Destructive actions use inline confirmation (iframe-safe; no window.confirm).
 */
import React, { useEffect, useState } from 'react';
import VisibilityToggle, { VisibilityBadge } from './VisibilityToggle';
import { formatBackendDetail } from '../lib/apiErrors';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_BODY = 2000;

function timeAgo(iso) {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function CompanyNotesPanel({ companyId, apiKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null); // note id
  const [isPrivate, setIsPrivate] = useState(false);

  const headers = apiKey
    ? { 'Content-Type': 'application/json', 'X-API-Key': apiKey }
    : { 'Content-Type': 'application/json' };

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API_URL}/api/companies/${companyId}/notes`, { headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setItems(d.items || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [companyId]);

  const add = async () => {
    const trimmed = body.trim();
    if (!trimmed) return;
    setSaving(true); setError(null);
    try {
      const r = await fetch(`${API_URL}/api/companies/${companyId}/notes`, {
        method: 'POST', headers,
        body: JSON.stringify({ body: trimmed, visibility: isPrivate ? 'private' : 'shared' }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(formatBackendDetail(d.detail) || `HTTP ${r.status}`);
      }
      setBody('');
      setIsPrivate(false);
      await load();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const doDelete = async (noteId) => {
    setSaving(true); setError(null);
    try {
      const r = await fetch(`${API_URL}/api/companies/${companyId}/notes/${noteId}`, {
        method: 'DELETE', headers,
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(formatBackendDetail(d.detail) || `HTTP ${r.status}`);
      }
      setPendingDelete(null);
      await load();
    } catch (e) { setError(e.message); setPendingDelete(null); }
    finally { setSaving(false); }
  };

  const changeVisibility = async (noteId, next) => {
    const r = await fetch(
      `${API_URL}/api/companies/${companyId}/notes/${noteId}/visibility`,
      {
        method: 'PATCH', headers,
        body: JSON.stringify({ visibility: next }),
      },
    );
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      throw new Error(formatBackendDetail(d.detail) || `HTTP ${r.status}`);
    }
    // Optimistic local update — avoid a full reload flicker.
    setItems((cur) => cur.map((n) => n.id === noteId ? { ...n, visibility: next } : n));
  };

  return (
    <div
      data-testid={`company-notes-${companyId}`}
      style={{ marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 8 }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6,
      }}>
        <span style={{
          fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
          letterSpacing: '0.04em', color: 'var(--text-secondary)',
        }}>
          Private notes
        </span>
        <span style={{ fontSize: 10, color: 'var(--text-secondary)' }}>
          {loading ? 'loading…' : `${items.length} note${items.length === 1 ? '' : 's'}`}
        </span>
      </div>

      {/* Add form */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'flex-start' }}>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value.slice(0, MAX_BODY))}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); add(); }
          }}
          placeholder="Pursuit notes — who you spoke to, what's next, context for the team…"
          data-testid={`company-note-input-${companyId}`}
          style={{
            flex: 1, minHeight: 52, fontSize: 12, padding: 6,
            border: '1px solid var(--border)', borderRadius: 4, resize: 'vertical',
            fontFamily: 'inherit',
          }}
        />
        <button
          className="btn btn-primary btn-sm"
          disabled={saving || !body.trim()}
          onClick={add}
          data-testid={`company-note-add-${companyId}`}
          style={{ alignSelf: 'stretch' }}
        >
          {saving ? '…' : 'Add'}
        </button>
      </div>
      <label
        data-testid={`company-note-private-toggle-${companyId}`}
        title="Private notes are visible only to you. Shared notes are visible to the tenant team."
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          fontSize: 10, color: 'var(--text-secondary)', cursor: 'pointer', userSelect: 'none',
          marginBottom: 6,
        }}
      >
        <input
          type="checkbox"
          checked={isPrivate}
          onChange={(e) => setIsPrivate(e.target.checked)}
          data-testid={`company-note-private-checkbox-${companyId}`}
          style={{ margin: 0 }}
        />
        Save as private
      </label>
      {body.length > MAX_BODY - 100 && (
        <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: -4, marginBottom: 6 }}>
          {MAX_BODY - body.length} characters remaining
        </div>
      )}
      {error && (
        <div className="auth-error" data-testid={`company-note-error-${companyId}`}>{error}</div>
      )}

      {/* List */}
      {!loading && items.length === 0 && (
        <div style={{ fontSize: 11, color: 'var(--text-secondary)', padding: '6px 0', fontStyle: 'italic' }}
             data-testid={`company-notes-empty-${companyId}`}>
          No notes yet. Drop context here so the next conversation picks up where the last one left off.
        </div>
      )}

      {items.map((n) => (
        <div key={n.id}
             data-testid={`company-note-${n.id}`}
             style={{
               padding: '8px 10px', marginBottom: 6,
               background: 'var(--bg)', border: '1px solid var(--border)',
               borderRadius: 6, fontSize: 12,
             }}>
          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{n.body}</div>
          <div style={{
            marginTop: 6, display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between',
            fontSize: 10, color: 'var(--text-secondary)',
          }}>
            <span>
              {n.created_by_email || '—'} · {timeAgo(n.created_at)}
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {n.is_mine ? (
                <VisibilityToggle
                  visibility={n.visibility}
                  canEdit={true}
                  onChange={(next) => changeVisibility(n.id, next)}
                  compact
                  testid={`company-note-visibility-${n.id}`}
                />
              ) : (
                <VisibilityBadge
                  visibility={n.visibility}
                  compact
                  testid={`company-note-visibility-badge-${n.id}`}
                />
              )}
              {n.can_delete && pendingDelete !== n.id && (
              <button
                className="btn btn-outline btn-sm"
                onClick={() => setPendingDelete(n.id)}
                data-testid={`company-note-delete-${n.id}`}
                style={{ fontSize: 9, padding: '2px 8px', color: 'var(--danger)' }}
              >
                Delete
              </button>
            )}
            {pendingDelete === n.id && (
              <span style={{ display: 'flex', gap: 4 }}
                    data-testid={`company-note-delete-confirm-${n.id}`}>
                <button className="btn btn-primary btn-sm"
                        onClick={() => doDelete(n.id)}
                        disabled={saving}
                        data-testid={`company-note-delete-confirm-yes-${n.id}`}
                        style={{ fontSize: 9, padding: '2px 8px', background: 'var(--danger)' }}>
                  Yes, delete
                </button>
                <button className="btn btn-outline btn-sm"
                        onClick={() => setPendingDelete(null)}
                        data-testid={`company-note-delete-cancel-${n.id}`}
                        style={{ fontSize: 9, padding: '2px 8px' }}>
                  Cancel
                </button>
              </span>
            )}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}
